import "./qa-premium.css";
import { useTranslation } from "react-i18next";
import { useRoute, useLocation } from "wouter";
import { trpc } from "@/utils/trpc";
import { toast } from "sonner";
import { VISIT_STATUS_META, BAND_META, formatDate, useQaPerms, QaCrumb, type QaVisitStatus } from "./qa-shared";
import { CalendarPlus, FileCheck, Eye, ClipboardList, CircleCheck, FileBarChart, Ban, GitCommitVertical, type LucideIcon } from "lucide-react";

const ORDER: QaVisitStatus[] = ["scheduled", "questionnaire_submitted", "under_review", "assessment_submitted", "report_approved"];

export default function QaVisitDetail() {
  const { t } = useTranslation();
  const [, params] = useRoute("/qa/visits/:id");
  const [, setLocation] = useLocation();
  const id = params?.id ? Number(params.id) : undefined;
  const perms = useQaPerms();

  const { data: v, isLoading, refetch } = trpc.qa.visits.getById.useQuery({ id: id! }, { enabled: !!id });
  const startReview = trpc.qa.qag.startReview.useMutation({ onSuccess: () => { toast.success(t("qa.visitDetail.reviewStarted")); refetch(); } });
  const cancel = trpc.qa.visits.cancel.useMutation({ onSuccess: () => { toast.success(t("qa.visitDetail.visitCancelled")); refetch(); } });

  if (isLoading) return <div className="qa-premium full"><div className="wrap"><div className="loading"><span className="spin" /></div></div></div>;
  if (!v) return <div className="qa-premium full"><div className="wrap" style={{ paddingTop: 24 }}><div className="empty">Visit not found.</div></div></div>;

  const status = v.status as QaVisitStatus;
  const ci = ORDER.indexOf(status);
  const sMeta = VISIT_STATUS_META[status] ?? VISIT_STATUS_META.scheduled;
  const band = v.interpretationBand ? BAND_META[v.interpretationBand] : null;
  const hasScore = v.overallScore != null && (status === "report_approved" || status === "assessment_submitted");

  const stateFor = (i: number): "done" | "cur" | "todo" => {
    if (status === "cancelled") return "todo";
    if (status === "report_approved" && i === 4) return "done";
    if (i < ci) return "done";
    if (i === ci) return "cur";
    return "todo";
  };

  const events: { Icon: LucideIcon; label: string; detail: string }[] = [
    { Icon: CalendarPlus, label: t("qa.status.scheduled"), detail: `${formatDate(v.visitDate)}${v.scheduledByName ? ` · ${t("qa.visitDetail.by")} ${v.scheduledByName}` : ""}` },
    { Icon: FileCheck, label: t("qa.visitDetail.questionnaireSubmitted"), detail: v.questionnaireSubmittedByName ? `${t("qa.visitDetail.submittedBy")} ${v.questionnaireSubmittedByName}` : t("qa.visitDetail.preVisitQuestionnaire") },
    { Icon: Eye, label: t("qa.visitDetail.qagReview"), detail: t("qa.visitDetail.preVisitReviewByQag") },
    { Icon: ClipboardList, label: t("qa.visitDetail.onSiteAssessment"), detail: v.auditorName ? `${t("qa.visitDetail.conductedBy")} ${v.auditorName}` : t("qa.visitDetail.onSiteRatings") },
    { Icon: status === "report_approved" ? CircleCheck : FileBarChart, label: status === "report_approved" ? t("qa.visitDetail.reportApproved") : t("qa.visitDetail.report"), detail: status === "report_approved" ? `${v.reportApprovedByName ? `${t("qa.visitDetail.approvedBy")} ${v.reportApprovedByName} · ` : ""}${v.overallScore != null ? `${Math.round(Number(v.overallScore))}% · ${band?.label ?? ""}` : ""}` : t("qa.visitDetail.awaitingReport") },
  ];

  type Act = { label: string; icon: string; show: boolean; onClick: () => void; primary?: boolean; danger?: boolean };
  const acts: Act[] = [
    { label: status === "scheduled" ? t("qa.visitDetail.openQuestionnaire") : t("qa.visitDetail.viewQuestionnaire"), icon: "ti-file-check", show: perms.questionnaireView || perms.questionnaireEdit, primary: status === "scheduled", onClick: () => setLocation(`/qa/visits/${id}/questionnaire`) },
    { label: t("qa.visitDetail.startReview"), icon: "ti-player-play", show: status === "questionnaire_submitted" && perms.reviewManage, primary: true, onClick: () => startReview.mutate({ visitId: id! }) },
    { label: t("qa.visitDetail.qagReview"), icon: "ti-eye", show: ["questionnaire_submitted", "under_review", "assessment_submitted", "report_approved"].includes(status) && perms.reviewView, onClick: () => setLocation(`/qa/visits/${id}/review`) },
    { label: t("qa.visitDetail.onSiteAssessment"), icon: "ti-clipboard-list", show: ["under_review", "assessment_submitted", "report_approved"].includes(status) && perms.assessmentView, primary: status === "under_review", onClick: () => setLocation(`/qa/visits/${id}/assessment`) },
    { label: t("qa.visitDetail.report"), icon: "ti-file-bar", show: ["assessment_submitted", "report_approved"].includes(status) && perms.reportView, primary: status === "assessment_submitted" || status === "report_approved", onClick: () => setLocation(`/qa/visits/${id}/report`) },
    { label: t("qa.visitDetail.auditTrail"), icon: "ti-history", show: true, onClick: () => setLocation(`/qa/visits/${id}/audit`) },
    { label: t("qa.visitDetail.cancelVisit"), icon: "ti-x", show: perms.visitManage && status !== "report_approved" && status !== "cancelled", danger: true, onClick: () => { if (confirm(t("qa.visitDetail.confirmCancelVisit"))) cancel.mutate({ id: id! }); } },
  ];
  const shownActs = acts.filter((a) => a.show);

  return (
    <div className="qa-premium full">
      <div className="wrap" style={{ paddingTop: 22 }}>
        <QaCrumb backTo="/qa/visits" items={[{ label: "Visits", to: "/qa/visits" }, { label: v.visitNumber, mono: true }]} />

        <div className="vd-layout">
          {/* Timeline */}
          <div className="vd-card">
            <div className="vd-ct"><GitCommitVertical size={16} style={{ color: "var(--p)" }} /> Visit timeline — <span className="num" style={{ color: "var(--p)", fontFamily: "ui-monospace,monospace", fontSize: 13 }}>{v.visitNumber}</span></div>
            {events.map((e, i) => {
              const st = stateFor(i);
              return (
                <div key={i} className={`vd-ev ${st}`}>
                  <div className="ic"><e.Icon size={16} /></div>
                  <div>
                    <div className="et">{e.label}</div>
                    {e.detail && <div className="em">{e.detail}</div>}
                  </div>
                </div>
              );
            })}
            {status === "cancelled" && (
              <div className="vd-ev cur" style={{ marginTop: 6 }}>
                <div className="ic" style={{ background: "var(--gray-pale)", color: "var(--text3)" }}><Ban size={16} /></div>
                <div><div className="et" style={{ color: "var(--text2)" }}>Cancelled</div>{v.cancelReason && <div className="em">{v.cancelReason}</div>}</div>
              </div>
            )}
          </div>

          {/* Aside */}
          <div className="vd-aside">
            {hasScore && (
              <div className="vd-score">
                <div className="vd-ring" style={{ background: `conic-gradient(${band?.color ?? "var(--green)"} 0 ${Math.round(Number(v.overallScore))}%, #E3DCEE ${Math.round(Number(v.overallScore))}% 100%)` }}>
                  <div className="rin"><b style={{ color: band?.color ?? "var(--green)" }}>{Math.round(Number(v.overallScore))}%</b><span>Score</span></div>
                </div>
                <div>
                  <div className="band" style={{ color: band?.color ?? "var(--green)" }}>{band?.label ?? "Scored"}</div>
                  <div className="bl">{status === "report_approved" ? "Report approved" : "Assessment submitted"}</div>
                </div>
              </div>
            )}

            <div className="vd-panel">
              <div className="ph">Details <span className="vd-statuspill" style={{ background: sMeta.bg, color: sMeta.color }}>{sMeta.label}</span></div>
              <div className="vd-mrow"><span className="mk">Site</span><span className="mv">{v.siteName ?? `#${v.siteId}`}</span></div>
              <div className="vd-mrow"><span className="mk">Visit date</span><span className="mv">{formatDate(v.visitDate)}</span></div>
              <div className="vd-mrow"><span className="mk">DC Representative</span><span className="mv">{v.dcRepresentative ?? "—"}</span></div>
              <div className="vd-mrow"><span className="mk">QAG Auditor</span><span className="mv">{v.auditorName ?? "—"}</span></div>
              <div className="vd-mrow"><span className="mk">QAG Lead</span><span className="mv">{v.leadName ?? "—"}</span></div>
              <div className="vd-mrow"><span className="mk">Template</span><span className="mv">{v.template?.name ?? "—"} · v{v.version?.versionNumber ?? "?"}</span></div>
            </div>

            <div className="vd-panel">
              <div className="ph">Actions</div>
              <div className="vd-acts">
                {shownActs.map((a) => (
                  <button key={a.label} className={`vbtn ${a.primary ? "primary" : ""} ${a.danger ? "danger" : ""}`} onClick={a.onClick}>
                    <i className={`ti ${a.icon}`} /> {a.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div style={{ height: 30 }} />
      </div>
    </div>
  );
}